import { Injectable } from '@angular/core';
import { ApiService } from './api/api.service';
import { Observable, map } from 'rxjs';
import {
    LockdownRule,
    LockPolicy,
    LockPolicyRuleValidation,
    LockPolicyAttributes,
    LockPolicyRun,
} from '../_models/lock-policy';
export interface QueryParams { [key: string]: any; }
import { SearchQueryOptions } from './api/search-query-options';
import { ListResponse, SingleResponse } from './api/response-types';
import { HttpClient } from '@angular/common/http';
import * as utils from '../lib/utils';

@Injectable({
    providedIn: 'root',
})
export class LockPolicyService {
    private get policyModel() {
        return this.apiService.lock_policy;
    }

    constructor(
        private apiService: ApiService,
        private http: HttpClient
        ) {}

    private mapToSearchQueryOptions(params?: QueryParams): SearchQueryOptions {
        const options = new SearchQueryOptions();
        if (!params) {
            options.page_size = 25;
            options.page_number = 1;
            return options;
        };

        options.page_number = params['page'] ?? 1;
        options.page_size = params['page_size'] ?? 25;

        if (params['sort']) {
            options.sort = params['sort'];
        }
        if (params['filter'] && typeof params['filter'] === 'string' && params['filter'].length > 0) {
            options.filters = [{ name: 'name', op: 'ilike', val: `%${params['filter']}%` }];
        }

        return options;
    }

    getPolicies(params?: QueryParams): Observable<{
        policies: LockPolicy[];
        meta: { count: number; page: number; page_size: number } | undefined;
    }> {
        const options = this.mapToSearchQueryOptions(params);
        return this.policyModel
            .searchMany(options)
            .pipe(
                map((response: ListResponse<any>) => {
                    const policies = response.data.map((item: any) => {
                        const policy = new LockPolicy();
                        policy.id = item.id;
                        Object.assign(policy.attributes, item.attributes);
                        if (item.relationships) {
                            Object.assign(policy.relationships, item.relationships);
                        }
                        return policy;
                    });
                    const meta = response.meta ? {
                        count: response.meta.count ?? 0,
                        page: options.page_number,
                        page_size: options.page_size
                    } : undefined;
                    return { policies, meta };
                })
            );
    }

    getPolicy(id: string): Observable<LockPolicy> {
        return this.policyModel
            .getById(id)
            .pipe(
                map((response: SingleResponse<any>) => {
                    const policy = new LockPolicy();
                    policy.id = response.data.id;
                    Object.assign(policy.attributes, response.data.attributes);
                    if (response.data.relationships) {
                        Object.assign(policy.relationships, response.data.relationships);
                    }
                    return policy;
                })
            );
    }

    createPolicy(
        policy: LockPolicyAttributes
    ): Observable<LockPolicy> {
        const payload = {
            type: this.policyModel.modelName,
            attributes: policy,
        };
        return this.policyModel
            .obsSave(payload)
            .pipe(
                map((response: SingleResponse<any>) => {
                    const newPolicy = new LockPolicy();
                    newPolicy.id = response.data.id;
                    Object.assign(newPolicy.attributes, response.data.attributes);
                    if (response.data.relationships) {
                        Object.assign(newPolicy.relationships, response.data.relationships);
                    }
                    return newPolicy;
                })
            );
    }

    updatePolicy(
        id: string,
        policy: Partial<LockPolicyAttributes>
    ): Observable<LockPolicy> {
        const payload = {
            type: this.policyModel.modelName,
            id: id,
            attributes: policy,
        };
        return this.policyModel
            .obsPatch(payload)
            .pipe(
                map((response: SingleResponse<any>) => {
                    const updatedPolicy = new LockPolicy();
                    updatedPolicy.id = response.data.id;
                    Object.assign(updatedPolicy.attributes, response.data.attributes);
                    if (response.data.relationships) {
                        Object.assign(updatedPolicy.relationships, response.data.relationships);
                    }
                    return updatedPolicy;
                })
            );
    }

    deletePolicy(id: string): Observable<void> {
        return this.policyModel.obsDelete(id).pipe(map(() => undefined));
    }

    getPolicyLockTemplates(policyId: string, params?: QueryParams): Observable<{ templates: any[], meta: any }> {
        const baseUrl = this.policyModel['baseUrl'];
        const url = `${baseUrl}/${policyId}/lock_templates`;
        const httpParams = params ? utils.httpParamSerializer(params) : undefined;

        return this.http.get<any>(url, { params: httpParams }).pipe(
            map(response => ({
                templates: response.data.map(item => ({
                    id: item.id,
                    ...(item.attributes as any)
                })),
                meta: response.meta
            }))
        );
    }

    validateRule(rule: LockdownRule, exclusions?: any[], attributesToLock?: string[]): Observable<LockPolicyRuleValidation> {
        return this.apiService.getLockPolicyRuleValidation(rule, exclusions, attributesToLock);
    }

    getPolicyRuns(policyId: string, params?: QueryParams): Observable<{
        runs: LockPolicyRun[];
        meta: { count: number; page: number; page_size: number } | undefined;
    }> {
        const baseUrl = this.policyModel['baseUrl'];
        const url = `${baseUrl}/${policyId}/runs`;

        // Build query parameters
        const httpParams: any = {};
        if (params) {
            if (params['page']) {
                httpParams['page[number]'] = params['page'];
            }
            if (params['page_size']) {
                httpParams['page[size]'] = params['page_size'];
            }
            if (params['sort']) {
                httpParams['sort'] = params['sort'];
            }
        }

        return this.http.get<any>(url, { params: httpParams }).pipe(
            map(response => {
                const runs = (response.data || []).map(item => {
                    const run = new LockPolicyRun();
                    run.id = item.id;
                    run.policy_id = item.attributes.policy_id;
                    run.start_time = item.attributes.start_time;
                    run.end_time = item.attributes.end_time;
                    run.data = item.attributes.data;
                    return run;
                });

                const meta = response.meta ? {
                    count: response.meta.count ?? 0,
                    page: params?.['page'] ?? 1,
                    page_size: params?.['page_size'] ?? 20
                } : undefined;

                return { runs, meta };
            })
        );
    }

    getPolicyRunJobs(policyId: string, runId: string): Observable<any[]> {
        const baseUrl = this.policyModel['baseUrl'];
        const url = `${baseUrl}/${policyId}/runs/${runId}/jobs`;
        return this.http.get<any>(url).pipe(
            map(response => {
                return (response.data || []).map(item => ({
                    id: item.id,
                    ...(item.attributes || {})
                }));
            })
        );
    }
}
